import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/shadcn/ui/card";
import { Input } from "@/lib/shadcn/ui/input";
import { Label } from "@/lib/shadcn/ui/label";
import { Button } from "@/lib/shadcn/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/lib/shadcn/ui/select";
import { useAppData } from "@/hooks/useAppData";
import { MultiSelect } from "@/lib/shadcn/components/multi-select";
import { TagMultiSelect } from "@/lib/shadcn/components/tag-multi-select";
import { DateInput } from "@/lib/shadcn/components/date-input";
import { Badge } from "@/lib/shadcn/ui/badge";
import { DecommissionApartmentDialog } from "@/lib/shadcn/components/decommission-apartment-dialog";
import { ReactivateApartmentDialog } from "@/lib/shadcn/components/reactivate-apartment-dialog";
import { formatDateToISODate } from "@/utils/dates";
import { toast } from "@/lib/shadcn/hooks/use-toast";
import SearchableSelect from "@/lib/shadcn/components/searchable-select";
import PercentageInput from "@/lib/shadcn/components/percentageInput";
import { HostHubRentalSelector } from "@/lib/shadcn/components/hosthub-rental-selector";
import { NeteraAccountSelect } from "@/lib/shadcn/components/netera-account-select";
import { ApartmentUpdate, useApartment, useGetNeteraCredentialLabelsQuery, usePutApartmentMutation } from "@/store/slices/apartments";
import { AddressSelect } from "@/lib/shadcn/components/address-select";
import { isValidHttpUrl } from "@/pages/is-valid-http-url";
import { hasAddress } from "@/pages/new-apartment-validation";
import { SaveChangesButton } from "@/pages/save-changes-button";

export default function ApartmentDetailsPage() {
    const { id } = useParams();
    const apartment = useApartment(Number(id));
    const [putApartment] = usePutApartmentMutation();
    const [formData, setFormData] = useState<ApartmentUpdate>({
        ...apartment,
        effectiveFrom: new Date(),
    });
    const [apartmentOwners, setApartmentOwners] = useState<number[]>(apartment?.ownerIds || []);
    const [ownersChanged, setOwnersChanged] = useState(false);
    const { ownerOptions, ownerIdMap, groupIdMap, ownerGroupOptions } = useAppData();
    const { data: neteraLabels = [], isFetching: neteraLabelsLoading, isError: neteraLabelsError } = useGetNeteraCredentialLabelsQuery();
    const [isEditing, setIsEditing] = useState(false);
    const [decommissionOpen, setDecommissionOpen] = useState(false);
    const [reactivateOpen, setReactivateOpen] = useState(false);

    const handleInputChange = useCallback(<T extends keyof ApartmentUpdate>(field: T, value: ApartmentUpdate[T]) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    // Toggle edit mode. Leaving edit mode ("Cancel Editing") discards unsaved
    // edits by re-deriving formData from the source apartment — otherwise a
    // changed-but-cancelled Netera selection (or any field) would leak back in
    // on the next edit (AS-006 / ui-spec "reset on Cancel Editing").
    const handleToggleEditing = useCallback(() => {
        setIsEditing((editing) => {
            if (editing) {
                setFormData({ ...apartment, effectiveFrom: new Date() });
                setApartmentOwners(apartment?.ownerIds || []);
                setOwnersChanged(false);
            }
            return !editing;
        });
    }, [apartment]);

    const handleSaveChanges = useCallback(async () => {
        try {
            // `address` is dropped from the payload: it is the building's canonical
            // string, derived server-side from addressId (address-normalization/
            // FR-007, FR-015), not something this form owns.
            const updatedApartment = {
                ...formData,
                address: undefined,
                ownerIds: apartmentOwners,
            };

            await putApartment(updatedApartment).unwrap();

            toast({
                title: "Success",
                description: "Apartment details updated successfully",
                variant: "success",
            });

            setIsEditing(false);
        } catch (error) {
            // An in-flight mutation aborted because the component unmounted
            // (e.g. the operator navigated away mid-save) is not a real failure
            // — don't toast an error or log it. Mirrors new-apartment.tsx.
            if ((error as { name?: string } | null)?.name === "AbortError") {
                return;
            }
            toast({
                title: "Error",
                description: "Failed to update apartment details",
                variant: "destructive",
            });
            console.error("Failed to update apartment:", error);
        }
    }, [formData, apartmentOwners, putApartment]);

    if (!apartment) {
        return <div className="p-8">Loading...</div>;
    }

    const isDecommissioned = apartment.dateRemoved != null;
    // FR-014 applies to the EDIT path too ("created or updated"): an edit may not
    // drop the building. Blocking the save is the answer to give the operator —
    // the server refuses it as well, but as an opaque 400.
    const canSave = hasAddress(formData);
    // A dimmed button is not an answer either: an apartment recorded before the
    // buildings existed opens its form already blocked, and the operator has to
    // be told which field is doing it (WCAG 3.3.1).
    const saveBlockedReason = canSave ? undefined : "Cannot save yet — still required: Address.";

    return (
        <div className="container mx-auto py-6 space-y-8">
            <div className="bg-white rounded-lg shadow-sm">
                <div className="p-6">
                    <div className="flex flex-col md:flex-row gap-8"></div>
                </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold">{apartment.name}</h1>
                    <p className="text-gray-500">Manage apartment details and settings</p>
                    {isDecommissioned && (
                        <div
                            role="status"
                            data-testid="decommissioned-banner"
                            className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 rounded-md border bg-gray-50 p-3"
                        >
                            <Badge variant="secondary" className="w-fit">
                                Decommissioned
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                                This apartment was removed on {formatDateToISODate(apartment.dateRemoved!)} and is
                                hidden from active lists. Reactivate to restore it.
                            </span>
                        </div>
                    )}
                </div>
                <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                    <Button variant={isEditing ? "secondary" : "default"} onClick={handleToggleEditing}>
                        {isEditing ? "Cancel Editing" : "Edit Details"}
                    </Button>
                    {isEditing && (
                        <SaveChangesButton
                            reasonId="apartment-details-save-blocked-top"
                            reason={saveBlockedReason}
                            onClick={handleSaveChanges}
                        />
                    )}
                    {isDecommissioned ? (
                        <Button
                            variant="outline"
                            className="w-full sm:w-auto"
                            data-testid="reactivate-button"
                            aria-label={`Reactivate apartment ${apartment.name}`}
                            onClick={() => setReactivateOpen(true)}
                        >
                            Reactivate
                        </Button>
                    ) : (
                        <Button
                            variant="outline"
                            className="w-full sm:w-auto text-red-600 border-red-300 hover:text-red-700 hover:border-red-400"
                            data-testid="decommission-button"
                            aria-label={`Decommission apartment ${apartment.name}`}
                            onClick={() => setDecommissionOpen(true)}
                        >
                            Decommission
                        </Button>
                    )}
                </div>
            </div>

            <DecommissionApartmentDialog
                apartment={{ id: apartment.id, name: apartment.name, dateAdded: apartment.dateAdded }}
                open={decommissionOpen}
                onOpenChange={setDecommissionOpen}
            />
            <ReactivateApartmentDialog
                apartment={{ id: apartment.id, name: apartment.name }}
                open={reactivateOpen}
                onOpenChange={setReactivateOpen}
            />

            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* General Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle>General Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Apartment Name</Label>
                                    <Input id="name" value={formData.name} onChange={(e) => handleInputChange("name", e.target.value)} disabled={!isEditing} />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="shortName">Short Name</Label>
                                    <Input
                                        id="shortName"
                                        value={formData.shortName}
                                        onChange={(e) => handleInputChange("shortName", e.target.value)}
                                        disabled={!isEditing}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="beds">Number of Bedrooms</Label>
                                    <Input
                                        id="beds"
                                        type="number"
                                        value={formData.numberOfBeds}
                                        onChange={(e) => handleInputChange("numberOfBeds", Number(e.target.value))}
                                        disabled={!isEditing}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="squareMeters">Square Meters</Label>
                                    <Input
                                        id="squareMeters"
                                        type="number"
                                        value={formData.squareMeters}
                                        onChange={(e) => handleInputChange("squareMeters", Number(e.target.value))}
                                        disabled={!isEditing}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-4">
                                    <Label htmlFor="address">Address*</Label>
                                    <AddressSelect
                                        id="address"
                                        required
                                        value={formData.addressId ?? null}
                                        onChange={(addressId) => handleInputChange("addressId", addressId)}
                                        disabled={!isEditing}
                                    />
                                </div>
                                <div className="space-y-4">
                                    <Label htmlFor="floor">Floor</Label>
                                    <Input
                                        id="floor"
                                        value={formData.floor}
                                        onChange={(e) => handleInputChange("floor", Number(e.target.value))}
                                        disabled={!isEditing}
                                    />
                                </div>

                                <div className="space-y-4">
                                    <Label htmlFor="dateAdded">Went Live On</Label>
                                    <DateInput
                                        id="dateAdded"
                                        value={formData.dateAdded}
                                        onChange={(date) => handleInputChange("dateAdded", date || new Date())}
                                        disabled={!isEditing}
                                    />
                                </div>
                                {/* The municipality section is no longer an apartment field
                                    (address-normalization/FR-020): it belongs to the building, so
                                    moving an apartment between sections means changing its address,
                                    or reassigning the address itself. */}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <PercentageInput
                                    id="checkInFeeRate"
                                    label="Check-in Fee Rate"
                                    value={(formData.checkInFeeRate ?? 0.065) * 100}
                                    onChange={(value) => handleInputChange("checkInFeeRate", value / 100)}
                                    disabled={!isEditing}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="h-fit max-w-full">
                        <CardHeader>
                            <CardTitle>Picture</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex-1 gap-2 ">
                                <div className="space-y-2 mb-2">
                                    <Label htmlFor="ImageUrl">Image Url*</Label>
                                    <Input
                                        id="ImageUrl"
                                        value={formData.imageUrl}
                                        placeholder={"https://reviveyouthandfamily.org/wp-content/uploads/2016/11/house-placeholder.jpg"}
                                        onChange={(e) => handleInputChange("imageUrl", e.target.value)}
                                        disabled={!isEditing}
                                    />
                                </div>
                                <div className="relative aspect-video w-full bg-gray-100 rounded-lg overflow-hidden ">
                                    <img
                                        alt={`${formData.imageUrl} Image`}
                                        src={
                                            isValidHttpUrl(formData.imageUrl || "")
                                                ? formData.imageUrl
                                                : "https://reviveyouthandfamily.org/wp-content/uploads/2016/11/house-placeholder.jpg"
                                        }
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.src = "/api/placeholder/800/600";
                                        }}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Ownership & Group */}
                    <Card className="h-fit max-w-full">
                        <CardHeader>
                            <CardTitle>Ownership & Group</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-[2fr_1fr_1fr] gap-4">
                                <div className="space-y-2 min-w-0">
                                    <Label>Owners</Label>
                                    <div className={""}>
                                        <MultiSelect
                                            options={ownerOptions}
                                            disabled={!isEditing}
                                            staticText={apartmentOwners.map((id) => ownerIdMap.get(id)?.fullName).join(", ")}
                                            selectedValues={apartmentOwners.map(String)}
                                            onChange={(values) => {
                                                const newSelectedOwners = values.map(Number);
                                                const ownersChanged = JSON.stringify(apartment.ownerIds) !== JSON.stringify(newSelectedOwners);

                                                setApartmentOwners(newSelectedOwners);
                                                setOwnersChanged(ownersChanged);
                                            }}
                                            placeholder={"No owners"}
                                        />
                                    </div>
                                </div>
                                {ownersChanged && (
                                    <div className="space-y-2">
                                        <Label htmlFor={"ownershipChangeFromDate"}>Effective From</Label>
                                        <DateInput
                                            id="ownershipChangeFromDate"
                                            value={formData.effectiveFrom}
                                            onChange={(date) => handleInputChange("effectiveFrom", date || undefined)}
                                            disabled={!isEditing}
                                        />
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <Label htmlFor={"Owner Group"}>Owner Group</Label>
                                    <SearchableSelect
                                        variant="outline"
                                        onChange={(value) => handleInputChange("ownerGroupId", Number(value))}
                                        disabled={!isEditing}
                                        placeholder="Owner Group"
                                        options={ownerGroupOptions}
                                        selectedValue={formData.ownerGroupId.toString()}
                                        width="w-full"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Group</Label>
                                <Select disabled={!isEditing} onValueChange={(value) => handleInputChange("groupId", Number(value))}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={groupIdMap.get(apartment.groupId)?.name} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Array.from(groupIdMap.entries()).map(([id, group]) => (
                                            <SelectItem key={id} value={id.toString()}>
                                                {group.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Tags</Label>
                                <TagMultiSelect
                                    selectedTagIds={formData.tagIds || []}
                                    onChange={(tagIds) => handleInputChange("tagIds", tagIds)}
                                    placeholder="Select tags..."
                                    disabled={!isEditing}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Documents & Certificates */}
                    <Card className="h-fit">
                        <CardHeader>
                            <CardTitle>Documents & Certificates</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                                <div className="space-y-2 col-span-3">
                                    <Label htmlFor="energyCertificateIssuanceDate">Energy Certificate Issuance Date</Label>
                                    <DateInput
                                        id="energyCertificateIssuanceDate"
                                        value={formData.energyCertificateIssuanceDate}
                                        onChange={(date) => handleInputChange("energyCertificateIssuanceDate", date || undefined)}
                                        disabled={!isEditing}
                                    />
                                </div>

                                <div className={"space-y-2 col-span-3"}>
                                    <Label htmlFor="atak">Short Term Registry ID</Label>
                                    <Input
                                        id=""
                                        value={formData.shortTermRegistryId}
                                        onChange={(e) => handleInputChange("shortTermRegistryId", e.target.value)}
                                        disabled={!isEditing}
                                    />
                                </div>
                                <div className="space-y-2 col-span-3">
                                    <Label htmlFor="initialTaxisRentalAgreement">Initial Rental Contract Taxis</Label>
                                    <Input
                                        id="initialTaxisRentalAgreement"
                                        value={formData.initialTaxisRentalAgreement}
                                        onChange={(e) => handleInputChange("initialTaxisRentalAgreement", e.target.value)}
                                        disabled={!isEditing}
                                    />
                                </div>
                                <div className="space-y-2 col-span-3">
                                    <Label htmlFor="initialTaxisRentalAgreement">Latest Rental Contract Taxis</Label>
                                    <Input
                                        id="latestTaxisRentalAgreement"
                                        value={formData.latestRentalTaxisAgreement}
                                        onChange={(e) => handleInputChange("latestRentalTaxisAgreement", e.target.value)}
                                        disabled={!isEditing}
                                    />
                                </div>
                                <div className="space-y-2 col-span-2">
                                    <Label htmlFor="disinfectionCertificateNumber">Disinfection Certificate Number</Label>
                                    <Input
                                        id="disinfectionCertificateNumber"
                                        value={formData.disinfectionCertificateNumber}
                                        onChange={(e) => handleInputChange("disinfectionCertificateNumber", e.target.value)}
                                        disabled={!isEditing}
                                    />
                                </div>
                                <div className="space-y-2 col-span-2">
                                    <Label htmlFor="disinfectionDate">
                                        Disinfection <br /> Date
                                    </Label>
                                    <DateInput
                                        id="disinfectionDate"
                                        value={formData.disinfectionDate}
                                        onChange={(date) => handleInputChange("disinfectionDate", date || undefined)}
                                        disabled={!isEditing}
                                    />
                                </div>
                                <div className="space-y-2 col-span-2">
                                    <Label htmlFor="fireExtinguisherRefillCertificateDate">Fire Extinguisher Refill Certificate Date</Label>
                                    <DateInput
                                        id="fireExtinguisherRefillCertificateDate"
                                        value={formData.fireExtinguisherRefillCertificateDate}
                                        onChange={(date) => handleInputChange("fireExtinguisherRefillCertificateDate", date || undefined)}
                                        disabled={!isEditing}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Utilities - Part 1 */}
                    <Card className="flex flex-col gap-4 justify-between">
                        <CardHeader>
                            <CardTitle>Internet & ATAK</CardTitle>
                        </CardHeader>
                        <CardContent className={"flex flex-col justify-between gap-4"}>
                            <div className={"flex flex-row gap-4"}>
                                <div className="w-[50%]">
                                    <div>
                                        <Label htmlFor="internet">Internet Company</Label>
                                        <Input
                                            id="internet"
                                            value={formData.internetCompany}
                                            onChange={(e) => handleInputChange("internetCompany", e.target.value)}
                                            disabled={!isEditing}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="internetLine">Internet Line</Label>
                                        <Input
                                            id="internetLine"
                                            value={formData.internetLine}
                                            onChange={(e) => handleInputChange("internetLine", e.target.value)}
                                            disabled={!isEditing}
                                        />
                                    </div>
                                </div>
                                <div className="w-[50%]">
                                    <div>
                                        <Label htmlFor="internet">Internet Username</Label>
                                        <Input
                                            id="internet"
                                            value={formData.internetUsername}
                                            onChange={(e) => handleInputChange("internetUsername", e.target.value)}
                                            disabled={!isEditing}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="internetLine">InternetPassword</Label>
                                        <Input
                                            id="internetPassword"
                                            value={formData.internetPassword}
                                            onChange={(e) => handleInputChange("internetPassword", e.target.value)}
                                            disabled={!isEditing}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={"flex flex-row gap-4"}>
                                <div className={"w-[50%]"}>
                                    <Label htmlFor="atak">ATAK</Label>
                                    <Input id="atak" value={formData.atak} onChange={(e) => handleInputChange("atak", e.target.value)} disabled={!isEditing} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Utilities - Part 2 */}
                    <Card className="h-fit">
                        <CardHeader>
                            <CardTitle>Electricity</CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            <div className="space-y-6 md:col-span-1">
                                <Label htmlFor="Electricity Company">Company</Label>
                                <Input
                                    id="Electricity Company"
                                    value={formData.electricityCompany}
                                    onChange={(e) => handleInputChange("electricityCompany", e.target.value)}
                                    disabled={!isEditing}
                                />
                            </div>
                            <div className="space-y-6 md:col-span-3">
                                <Label htmlFor="electricityRf">RF Payment Code</Label>
                                <Input
                                    id="electricityRf"
                                    value={formData.electricityRf}
                                    onChange={(e) => handleInputChange("electricityRf", e.target.value)}
                                    disabled={!isEditing}
                                />
                            </div>
                            <div className="space-y-6 md:col-span-2">
                                <Label htmlFor="electricitySupplyId">Supply ID</Label>
                                <Input
                                    id="electricitySupplyId"
                                    value={formData.electricitySupplyId}
                                    onChange={(e) => handleInputChange("electricitySupplyId", e.target.value)}
                                    disabled={!isEditing}
                                />
                            </div>
                            <div className="space-y-6 md:col-span-2">
                                <Label htmlFor="electricityCertificateTerminationDate">Electricity Certificate Termination Date</Label>
                                <DateInput
                                    id="electricityCertificateTerminationDate"
                                    value={formData.electricityCertificateTerminationDate}
                                    onChange={(date) => handleInputChange("electricityCertificateTerminationDate", date || undefined)}
                                    disabled={!isEditing}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Utilities - Part 3 */}
                    <Card className="h-fit">
                        <CardHeader>
                            <CardTitle>Water (EYDAP)</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="eydapSupplyId">Supply ID</Label>
                                    <Input
                                        id="eydapSupplyId"
                                        value={formData.eydapSupplyId}
                                        onChange={(e) => handleInputChange("eydapSupplyId", e.target.value)}
                                        disabled={!isEditing}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="eydapRegistryId">Registry ID</Label>
                                    <Input
                                        id="eydapRegistryId"
                                        value={formData.eydapRegistryId}
                                        onChange={(e) => handleInputChange("eydapRegistryId", e.target.value)}
                                        disabled={!isEditing}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="h-fit">
                        <CardHeader>
                            <CardTitle>Insurance Contract</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="insuranceCompany">Insurance Company</Label>
                                    <Input
                                        id="insuranceCompany"
                                        value={formData.insuranceCompany}
                                        onChange={(e) => handleInputChange("insuranceCompany", e.target.value)}
                                        disabled={!isEditing}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="insuranceContractId">Insurance Contract ID</Label>
                                    <Input
                                        id="insuranceContractId"
                                        value={formData.insuranceContractId}
                                        onChange={(e) => handleInputChange("insuranceContractId", e.target.value)}
                                        disabled={!isEditing}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="h-fit">
                        <CardHeader>
                            <CardTitle>Platform Links</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="airbnbUrl">AirBnB Url</Label>
                                    <Input
                                        id="airbnbUrl"
                                        type="url"
                                        value={formData.airbnbUrl}
                                        onChange={(e) => handleInputChange("airbnbUrl", e.target.value)}
                                        disabled={!isEditing}
                                        placeholder="https://www.airbnb.com/..."
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="bookingUrl">Booking Url</Label>
                                    <Input
                                        id="bookingUrl"
                                        type="url"
                                        value={formData.bookingUrl}
                                        onChange={(e) => handleInputChange("bookingUrl", e.target.value)}
                                        disabled={!isEditing}
                                        placeholder="https://www.booking.com/..."
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="h-fit">
                        <CardHeader>
                            <CardTitle>HostHub Mapping</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <HostHubRentalSelector
                                rentalId={formData.hosthubRentalId}
                                excluded={formData.hosthubExcluded ?? false}
                                onChange={(rentalId) => handleInputChange("hosthubRentalId", rentalId)}
                                onExcludedChange={(excluded) => handleInputChange("hosthubExcluded", excluded)}
                                disabled={!isEditing}
                            />
                        </CardContent>
                    </Card>

                    <NeteraAccountSelect
                        value={formData.neteraCredentialsId ?? null}
                        onChange={(id) => handleInputChange("neteraCredentialsId", id)}
                        labels={neteraLabels}
                        isLoading={neteraLabelsLoading}
                        isError={neteraLabelsError}
                        readOnly={!isEditing}
                    />
                </div>

                {isEditing && (
                    <div className="flex justify-end space-x-4">
                        <Button variant="outline" onClick={handleToggleEditing}>
                            Cancel
                        </Button>
                        <SaveChangesButton
                            reasonId="apartment-details-save-blocked-bottom"
                            reason={saveBlockedReason}
                            onClick={handleSaveChanges}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
